import {Preferences} from '@capacitor/preferences';
import {addGlobalHeaderToApiClient, apiClient} from '@common/http/query-client';
import {
  decodeBootstrapData,
  setBootstrapData,
} from '@ui/bootstrap-data/bootstrap-data-store';
import {
  isNativePlatform,
  nativePlatform,
  requiredNativeApiOrigin,
} from './platform';
import {identifyPurchaser, logOutPurchaser} from './store';

const ACCESS_TOKEN_KEY = 'native_access_token';

function mirrorNativeAccessToken(value: string | null) {
  if (value) localStorage.setItem(ACCESS_TOKEN_KEY, value);
  else localStorage.removeItem(ACCESS_TOKEN_KEY);
}

function synchronizePurchaser(userId?: number | string) {
  if (!userId) return;
  // StoreKit/RevenueCat can be slow or unavailable on first launch. An
  // authenticated session must never be held behind provider identification;
  // purchase and restore surfaces still await identifyPurchaser themselves.
  void identifyPurchaser(userId).catch(error => {
    console.warn(
      '[native store identity]',
      error instanceof Error ? error.message : 'failed',
    );
  });
}

interface NativeMobileBootstrap {
  user?: {
    access_token?: string;
    id?: number | string;
  } | null;
}

interface NativePasswordLoginPayload {
  email: string;
  password: string;
  remember: boolean;
  token_name: string;
}

interface NativeRegistrationPayload {
  email: string;
  password: string;
  password_confirmation: string;
  invite_id?: string;
  invite_type?: string;
  captcha_token: string | null;
  minimum_age_confirmed?: boolean;
}

export function nativeApiUrl(path: string) {
  return new URL(
    `api/v1/${path.replace(/^\/+/, '')}`,
    `${requiredNativeApiOrigin()}/`,
  ).toString();
}

export async function restoreNativeSession() {
  if (!isNativePlatform()) return null;
  const mirroredValue = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (mirroredValue) {
    addGlobalHeaderToApiClient('Authorization', `Bearer ${mirroredValue}`);
    return mirroredValue;
  }

  // Migrate an existing native token before bootstrap, but never let a stuck
  // Capacitor bridge hold the launch screen indefinitely. New logins write the
  // webview mirror immediately, so this bounded bridge read is upgrade-only.
  const stored = await Promise.race([
    Preferences.get({key: ACCESS_TOKEN_KEY}).catch(() => ({value: null})),
    new Promise<{value: null}>(resolve =>
      window.setTimeout(() => resolve({value: null}), 1500),
    ),
  ]);
  if (stored.value) {
    mirrorNativeAccessToken(stored.value);
    addGlobalHeaderToApiClient('Authorization', `Bearer ${stored.value}`);
  }
  return stored.value;
}

async function persistNativeAccessToken(accessToken: string) {
  mirrorNativeAccessToken(accessToken);
  addGlobalHeaderToApiClient('Authorization', `Bearer ${accessToken}`);
  void Preferences.set({
    key: ACCESS_TOKEN_KEY,
    value: accessToken,
  }).catch(() => undefined);
}

export async function establishNativeSession(
  idToken: string,
  displayName?: string | null,
  minimumAgeConfirmed = false,
) {
  const response = await apiClient.post<{
    access_token: string;
    bootstrap_data: string;
  }>(nativeApiUrl('auth/firebase'), {
    id_token: idToken,
    token_name: `${nativePlatform()}-capacitor`,
    display_name: displayName || undefined,
    minimum_age_confirmed: minimumAgeConfirmed,
  });
  await persistNativeAccessToken(response.data.access_token);
  const bootstrap = decodeBootstrapData(response.data.bootstrap_data);
  setBootstrapData(bootstrap);
  synchronizePurchaser(bootstrap.user?.id);
  return bootstrap;
}

export async function establishNativePasswordSession(
  payload: NativePasswordLoginPayload,
) {
  const login = await apiClient.post<NativeMobileBootstrap>(
    nativeApiUrl('auth/login'),
    {
      ...payload,
      token_name: payload.token_name || `${nativePlatform()}-capacitor`,
    },
  );
  const accessToken = login.data.user?.access_token;
  if (!accessToken) {
    throw new Error('Native login did not return an access token.');
  }

  await persistNativeAccessToken(accessToken);
  const bootstrap = await apiClient.get<{data: string}>(
    nativeApiUrl('bootstrap-data'),
  );
  const decoded = decodeBootstrapData(bootstrap.data.data);
  synchronizePurchaser(decoded.user?.id);

  return {
    bootstrapData: bootstrap.data.data,
    two_factor: false as const,
  };
}

export async function establishNativeRegistrationSession(
  payload: NativeRegistrationPayload,
) {
  const response = await apiClient.post<{
    status: 'success' | 'needs_email_verification';
    bootstrapData: NativeMobileBootstrap;
    webBootstrapData: string;
  }>(nativeApiUrl('auth/register'), {
    ...payload,
    token_name: `${nativePlatform()}-capacitor`,
  });
  const accessToken = response.data.bootstrapData.user?.access_token;
  if (!accessToken) {
    throw new Error('Native registration did not return an access token.');
  }
  if (!response.data.webBootstrapData) {
    throw new Error('Native registration did not return SPA bootstrap data.');
  }

  await persistNativeAccessToken(accessToken);
  const decoded = decodeBootstrapData(response.data.webBootstrapData);
  synchronizePurchaser(decoded.user?.id);

  return {
    bootstrapData: response.data.webBootstrapData,
    status: response.data.status,
  };
}

export async function clearNativeSession() {
  if (!isNativePlatform()) return;
  await Promise.allSettled([
    apiClient.delete('push-devices', {data: {all: true}}),
    apiClient.post(nativeApiUrl('auth/native/logout')),
    logOutPurchaser(),
  ]);
  mirrorNativeAccessToken(null);
  void Preferences.remove({key: ACCESS_TOKEN_KEY}).catch(() => undefined);
  addGlobalHeaderToApiClient('Authorization', '');
}
